import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  FaEye,
  FaEyeSlash,
  FaGripVertical,
  FaImage,
  FaLink,
  FaSave,
  FaYoutube,
} from "react-icons/fa";

import {
  canEditMedia,
  getYoutubeId,
  isImageUrl,
  toMediaUrl,
} from "../utils/homeSectionsSettingsUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-5">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-28 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function MediaPreview({ section }) {
  const mediaURL = section?.mediaURL || "";
  const youtubeId = getYoutubeId(mediaURL);
  const image = isImageUrl(mediaURL);
  const finalUrl = toMediaUrl(mediaURL);

  return (
    <div className="h-32 w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800 sm:w-56">
      {mediaURL ? (
        youtubeId ? (
          <iframe
            className="h-full w-full"
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={section.section_name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : image ? (
          <img
            src={finalUrl}
            alt={section.section_name}
            className="h-full w-full object-cover"
            onError={(event) => {
              event.currentTarget.src = "/img/header_placeholder_1.jpg";
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-3 text-center text-xs text-slate-500 dark:text-slate-400">
            {mediaURL}
          </div>
        )
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-slate-400">
          Aucun média
        </div>
      )}
    </div>
  );
}

export default function HomeSectionsList({
  sections,
  loading,
  savingId,
  mediaInputs,
  onToggle,
  onDragEnd,
  onPositionChange,
  onMediaInputChange,
  onMediaSave,
}) {
  if (loading) return <SkeletonRows />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Sections
          </p>

          <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Ordre et visibilité
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Glissez les sections pour modifier leur ordre. Vous pouvez aussi
            saisir une position précise. Les médias modifiables concernent les
            contenus photo #01, #02 et vidéo #01.
          </p>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="home-sections-droppable">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-4"
              >
                {sections.map((section, index) => {
                  const inactive = !section.is_active;
                  const editableMedia = canEditMedia(section.id);
                  const youtubeId = getYoutubeId(section.mediaURL || "");

                  return (
                    <Draggable
                      key={`sec_${section.id}`}
                      draggableId={`sec_${section.id}`}
                      index={index}
                    >
                      {(drag) => (
                        <article
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className={`rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm transition dark:border-slate-800 dark:bg-slate-950/60 ${
                            inactive ? "opacity-70" : ""
                          }`}
                        >
                          <div className="flex flex-col gap-4 xl:flex-row">
                            <div
                              {...drag.dragHandleProps}
                              className="flex cursor-grab items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2 text-slate-500 active:cursor-grabbing dark:bg-slate-900 dark:text-slate-400 xl:flex-col xl:justify-center"
                              title="Glisser pour déplacer"
                            >
                              <FaGripVertical />
                              <span className="rounded-xl bg-white px-2 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                                #{section.position}
                              </span>
                            </div>

                            <MediaPreview section={section} />

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-lg font-bold text-slate-900 dark:text-white">
                                      {section.section_name}
                                    </h4>

                                    {section.is_active ? (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                                        <FaEye />
                                        actif
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                                        <FaEyeSlash />
                                        masqué
                                      </span>
                                    )}

                                    {youtubeId ? (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
                                        <FaYoutube />
                                        vidéo
                                      </span>
                                    ) : section.mediaURL ? (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                                        <FaImage />
                                        média
                                      </span>
                                    ) : null}
                                  </div>

                                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                                    {section.description || "Aucune description."}
                                  </p>

                                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                                    ID HTML : <code>#{section.slug}</code>
                                  </p>
                                </div>

                                <label className="inline-flex cursor-pointer select-none items-center gap-3">
                                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                    Visible
                                  </span>

                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={!!section.is_active}
                                    onChange={() => onToggle(section.id)}
                                    disabled={savingId === section.id}
                                  />

                                  <span
                                    className={`flex h-7 w-12 items-center rounded-full p-1 transition ${
                                      section.is_active
                                        ? "bg-emerald-500"
                                        : "bg-slate-300 dark:bg-slate-700"
                                    }`}
                                  >
                                    <span
                                      className={`h-5 w-5 rounded-full bg-white shadow transition ${
                                        section.is_active ? "translate-x-5" : ""
                                      }`}
                                    />
                                  </span>
                                </label>
                              </div>

                              <div className="mt-4 grid gap-3 lg:grid-cols-[160px,1fr]">
                                <div>
                                  <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                    Position
                                  </label>

                                  <input
                                    type="number"
                                    min={1}
                                    max={sections.length}
                                    defaultValue={section.position}
                                    onBlur={(event) => {
                                      if (Number(event.target.value) !== section.position) {
                                        onPositionChange(section.id, event.target.value);
                                      }
                                    }}
                                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                                  />
                                </div>

                                {editableMedia ? (
                                  <div>
                                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                      Média
                                    </label>

                                    <div className="flex flex-col gap-2 sm:flex-row">
                                      <div className="relative flex-1">
                                        <FaLink className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                                        <input
                                          type="url"
                                          value={mediaInputs[section.id] ?? ""}
                                          onChange={(event) =>
                                            onMediaInputChange(
                                              section.id,
                                              event.target.value
                                            )
                                          }
                                          placeholder={
                                            Number(section.id) === 19
                                              ? "Lien YouTube"
                                              : "Lien image"
                                          }
                                          inputMode="url"
                                          className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                                        />
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => onMediaSave(section)}
                                        disabled={savingId === section.id}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                                      >
                                        <FaSave />
                                        Changer
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                                    Média non modifiable depuis cet écran.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </article>
                      )}
                    </Draggable>
                  );
                })}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </section>
  );
}